import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const STATUSES = ["All", "Pending", "Approved", "Rejected"];
const EMPTY_FORM = {
  employeeId: "",
  leaveType: "Casual",
  startDate: "",
  endDate: "",
  reason: "",
};

function StatusBadge({ status }) {
  const tone =
    status === "Approved" ? "success" :
    status === "Pending" ? "warn" :
    status === "Rejected" ? "danger" :
    "neutral";

  return <span className={`hrms-badge ${tone}`}>{status}</span>;
}

export default function LeavesPage() {
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [status, setStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [employeesRes, leavesRes] = await Promise.all([
        apiFetch("/api/hrms/employees?status=Active"),
        apiFetch("/api/hrms/leaves"),
      ]);
      if (!employeesRes.ok) throw new Error(await employeesRes.text());
      if (!leavesRes.ok) throw new Error(await leavesRes.text());
      const nextEmployees = await employeesRes.json();
      const nextLeaves = await leavesRes.json();
      setEmployees(Array.isArray(nextEmployees) ? nextEmployees : []);
      setLeaves(Array.isArray(nextLeaves) ? nextLeaves : []);
    } catch (err) {
      setError(err?.message || "Could not load leave requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    return {
      All: leaves.length,
      Pending: leaves.filter((leave) => leave.status === "Pending").length,
      Approved: leaves.filter((leave) => leave.status === "Approved").length,
      Rejected: leaves.filter((leave) => leave.status === "Rejected").length,
    };
  }, [leaves]);

  const visibleLeaves = useMemo(() => {
    if (status === "All") return leaves;
    return leaves.filter((leave) => leave.status === status);
  }, [leaves, status]);

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      employeeId: employees[0]?.id || "",
    });
    setModalOpen(true);
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function createLeave(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/hrms/leaves", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          startDate: `${form.startDate}T00:00:00`,
          endDate: `${form.endDate}T00:00:00`,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create leave request.");
    } finally {
      setSaving(false);
    }
  }

  async function updateLeave(leaveId, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/hrms/leaves/${leaveId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update leave request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hrms-page">
      <PageHeader
        title="Leave Requests"
        subtitle="Create, approve, and reject employee leave requests."
        actions={<button className="primary" onClick={openCreate} disabled={employees.length === 0}>New Leave</button>}
      />

      <div className="glass-card" style={{ marginBottom: 16, padding: 14 }}>
        <div className="crm-stage-filter" aria-label="Leave status filter" style={{ justifyContent: "flex-start" }}>
          {STATUSES.map((item) => (
            <button
              key={item}
              type="button"
              className={status === item ? "active" : ""}
              onClick={() => setStatus(item)}
            >
              {item}
              <span>{counts[item] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card hrms-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : visibleLeaves.length === 0 ? (
          <EmptyState
            title={status === "All" ? "No leave requests yet" : `No ${status.toLowerCase()} leave requests`}
            subtitle={employees.length === 0 ? "Add active employees first." : "Create a leave request to start approval tracking."}
            action={employees.length > 0 ? <button className="primary" onClick={openCreate}>New Leave</button> : null}
          />
        ) : (
          <div className="hrms-table-wrap">
            <table className="hrms-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Period</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleLeaves.map((leave) => (
                  <tr key={leave.id}>
                    <td>
                      <div className="hrms-row-title">{leave.employeeName}</div>
                      <div className="hrms-row-subtitle">{leave.employeeCode}</div>
                    </td>
                    <td>{leave.leaveType}</td>
                    <td>{formatDate(leave.startDate)} to {formatDate(leave.endDate)}</td>
                    <td>{leave.days}</td>
                    <td>{leave.reason || "-"}</td>
                    <td><StatusBadge status={leave.status} /></td>
                    <td>
                      {leave.status === "Pending" ? (
                        <div className="hrms-action-row">
                          <button className="btn-success" onClick={() => updateLeave(leave.id, "Approved")} disabled={saving}>
                            Approve
                          </button>
                          <button className="btn-danger" onClick={() => updateLeave(leave.id, "Rejected")} disabled={saving}>
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="hrms-row-subtitle">Reviewed {formatDate(leave.reviewedAt)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createLeave} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>New Leave Request</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
                x
              </button>
            </div>
            <div className="crm-form-grid">
              <label className="crm-form-wide">
                Employee
                <select value={form.employeeId} onChange={(event) => updateField("employeeId", event.target.value)} required>
                  <option value="">Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName} ({employee.employeeCode})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Leave Type
                <select value={form.leaveType} onChange={(event) => updateField("leaveType", event.target.value)}>
                  <option>Casual</option>
                  <option>Sick</option>
                  <option>Earned</option>
                  <option>Unpaid</option>
                </select>
              </label>
              <label>
                Start Date
                <input type="date" value={form.startDate} onChange={(event) => updateField("startDate", event.target.value)} required />
              </label>
              <label>
                End Date
                <input type="date" value={form.endDate} onChange={(event) => updateField("endDate", event.target.value)} required />
              </label>
              <label className="crm-form-wide">
                Reason
                <textarea value={form.reason} onChange={(event) => updateField("reason", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Request"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
